import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Settings, LogOut, Save, Users, MessageSquare, Eye, EyeOff, RefreshCw,
  Shield, ShieldOff, Wifi, Globe, Video, TrendingUp, CalendarCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Login Screen ────────────────────────────────────────────────────────────

function LoginScreen({ onLogin }) {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem('ttt_admin_token', res.data.access_token);
      onLogin();
      toast.success('Welcome back, Adam.');
    } catch {
      toast.error('Invalid credentials. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#070A0F' }}>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.65 }}
        className="w-full max-w-sm"
      >
        <BrandMark />
        <div
          data-testid="admin-page"
          className="rounded-2xl p-8 mt-8"
          style={{ backgroundColor: '#0B1020', border: '1px solid rgba(212,175,55,0.18)' }}
        >
          <h1 className="text-xl font-bold mb-1" style={{ fontFamily: 'Space Grotesk', color: '#F4F6FA' }}>Admin Access</h1>
          <p className="text-sm mb-6" style={{ color: '#6B7B8E', fontFamily: 'IBM Plex Sans' }}>Enter your admin password to continue.</p>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label className="text-xs mb-1.5 block" style={{ color: '#C4CFD8', fontFamily: 'IBM Plex Sans' }}>Password</Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  className="h-11 rounded-xl pr-10"
                  style={{ backgroundColor: '#070A0F', borderColor: 'rgba(212,175,55,0.18)', color: '#F4F6FA' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(p => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  style={{ color: '#6B7B8E' }}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading || !password}
              className="w-full h-11 rounded-xl font-semibold"
              style={{ background: 'linear-gradient(135deg, #2563EB 0%, #1E40AF 60%, #1e3a8a 100%)', color: '#FFFFFF', fontFamily: 'IBM Plex Sans', border: 'none' }}
            >
              {loading ? 'Logging in...' : 'Login'}
            </Button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}

// ─── Brand Mark ──────────────────────────────────────────────────────────────

function BrandMark() {
  return (
    <div className="flex items-center gap-2.5">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #D4AF37, #B8922E)' }}>
        <span className="text-black font-bold text-sm" style={{ fontFamily: 'Space Grotesk' }}>T</span>
      </div>
      <div>
        <div className="text-sm font-semibold" style={{ fontFamily: 'Space Grotesk', color: '#F4F6FA' }}>TrepTrading</div>
        <div style={{ fontFamily: 'Azeret Mono', color: '#D4AF37', letterSpacing: '0.07em', fontSize: '9px', textTransform: 'uppercase' }}>Admin Panel</div>
      </div>
    </div>
  );
}

// ─── Stat Card ────────────────────────────────────────────────────────────────

function StatCard({ icon, label, value, color }) {
  return (
    <div className="rounded-2xl p-5" style={{ backgroundColor: '#0B1020', border: '1px solid rgba(212,175,55,0.15)' }}>
      <div className="flex items-center gap-2 mb-2">
        <span style={{ color }}>{icon}</span>
        <span className="text-xs" style={{ fontFamily: 'Azeret Mono', color: '#6B7B8E', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{label}</span>
      </div>
      <div className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk', color: '#F4F6FA' }}>{value}</div>
    </div>
  );
}

// ─── Main Admin Dashboard ─────────────────────────────────────────────────────

export default function AdminPage() {
  const [isLoggedIn, setIsLoggedIn] = useState(() => !!localStorage.getItem('ttt_admin_token'));
  const [activeTab, setActiveTab] = useState('leads');
  const [settings, setSettings] = useState({ youtube_url: '', calendly_url: '' });
  const [saveLoading, setSaveLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [rewindPending, setRewindPending] = useState({});
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    if (isLoggedIn) {
      fetchSettings();
      fetchStats();
      fetchLeads();
      fetchAnalytics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('ttt_admin_token')}` });

  const handleLogout = () => {
    localStorage.removeItem('ttt_admin_token');
    setIsLoggedIn(false);
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setSettings({ youtube_url: res.data.youtube_url || '', calendly_url: res.data.calendly_url || '' });
    } catch {}
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/stats`, { headers: authHeaders() });
      setStats(res.data);
    } catch (e) {
      if (e.response?.status === 401) handleLogout();
    }
  };

  const fetchLeads = async () => {
    setLeadsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/leads`, { headers: authHeaders() });
      setLeads(res.data);
    } catch (e) {
      if (e.response?.status === 401) handleLogout();
    } finally {
      setLeadsLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const res = await axios.get(`${API}/admin/analytics`, { headers: authHeaders() });
      setAnalytics(res.data);
    } catch (e) {
      if (e.response?.status === 401) handleLogout();
    }
  };

  const handleSave = async () => {
    setSaveLoading(true);
    try {
      await axios.put(`${API}/settings`, settings, { headers: authHeaders() });
      toast.success('Settings saved successfully!');
    } catch (e) {
      if (e.response?.status === 401) { toast.error('Session expired.'); handleLogout(); }
      else toast.error('Failed to save.');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleToggleRewind = async (lead) => {
    const key = lead.id;
    setRewindPending(p => ({ ...p, [key]: true }));
    try {
      if (lead.rewind_allowed) {
        await axios.post(`${API}/admin/rewind-whitelist/revoke`, { ip: lead.ip_address }, { headers: authHeaders() });
        toast.success(`Rewind revoked for ${lead.name}`);
      } else {
        await axios.post(`${API}/admin/rewind-whitelist`, {
          ip: lead.ip_address,
          lead_id: lead.id,
          name: lead.name,
          email: lead.email,
        }, { headers: authHeaders() });
        toast.success(`Rewind approved for ${lead.name}`);
      }
      setLeads(prev => prev.map(l => l.id === key ? { ...l, rewind_allowed: !l.rewind_allowed } : l));
    } catch {
      toast.error('Action failed. Please try again.');
    } finally {
      setRewindPending(p => ({ ...p, [key]: false }));
    }
  };

  if (!isLoggedIn) {
    return (
      <>
        <Toaster theme="dark" richColors position="top-right" />
        <LoginScreen onLogin={() => setIsLoggedIn(true)} />
      </>
    );
  }

  const tabs = [
    { id: 'leads', label: 'Leads & IPs' },
    { id: 'analytics', label: 'Analytics' },
    { id: 'settings', label: 'Settings' },
  ];

  return (
    <div className="min-h-screen px-4 py-8" style={{ backgroundColor: '#070A0F' }}>
      <Toaster theme="dark" richColors position="top-right" />
      <div className="max-w-5xl mx-auto">

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <BrandMark />
          <div className="flex items-center gap-2">
            <Button
              onClick={() => { fetchStats(); fetchLeads(); fetchAnalytics(); }}
              variant="ghost"
              className="h-9 px-3 rounded-xl"
              style={{ color: '#6B7B8E' }}
            >
              <RefreshCw size={14} />
            </Button>
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="text-sm inline-flex items-center gap-2 rounded-xl"
              style={{ color: '#6B7B8E', fontFamily: 'IBM Plex Sans' }}
            >
              <LogOut size={15} /> Logout
            </Button>
          </div>
        </div>

        {/* Stats Row */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <StatCard icon={<Users size={15} />} label="Webinar Leads" value={stats.total_leads} color="#1E40AF" />
            <StatCard icon={<MessageSquare size={15} />} label="Inquiries" value={stats.total_contacts} color="#D4AF37" />
            <StatCard icon={<Globe size={15} />} label="Landing Visits" value={analytics?.landing_visits ?? '—'} color="#22C55E" />
            <StatCard icon={<Video size={15} />} label="Webinar Visits" value={analytics?.webinar_visits ?? '—'} color="#A78BFA" />
          </div>
        )}

        {/* Tabs */}
        <div
          className="flex gap-1 mb-6 p-1 rounded-xl"
          style={{ backgroundColor: '#0B1020', border: '1px solid rgba(255,255,255,0.06)', width: 'fit-content' }}
        >
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
              style={{
                fontFamily: 'IBM Plex Sans',
                backgroundColor: activeTab === tab.id ? '#1E3A6E' : 'transparent',
                color: activeTab === tab.id ? '#FFFFFF' : '#6B7B8E',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── LEADS TAB ── */}
        {activeTab === 'leads' && (
          <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: '#0B1020', border: '1px solid rgba(212,175,55,0.12)' }}>
            <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
              <h2 className="text-base font-bold" style={{ fontFamily: 'Space Grotesk', color: '#F4F6FA' }}>Webinar Leads</h2>
              <span
                className="text-xs px-2.5 py-1 rounded-full"
                style={{ fontFamily: 'Azeret Mono', color: '#D4AF37', backgroundColor: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.15)' }}
              >
                {leads.length} total
              </span>
            </div>

            {leadsLoading ? (
              <div className="py-12 text-center text-sm" style={{ color: '#4B5563', fontFamily: 'IBM Plex Sans' }}>Loading leads...</div>
            ) : leads.length === 0 ? (
              <div className="py-12 text-center text-sm" style={{ color: '#4B5563', fontFamily: 'IBM Plex Sans' }}>No leads yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                      {['Name', 'Email', 'IP Address', 'Date', 'Rewind'].map(h => (
                        <th
                          key={h}
                          className="text-left px-6 py-3 text-xs font-medium"
                          style={{ fontFamily: 'Azeret Mono', color: '#4B5563', letterSpacing: '0.07em', textTransform: 'uppercase' }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((lead, i) => (
                      <tr key={lead.id || i} className="border-b last:border-0" style={{ borderColor: 'rgba(255,255,255,0.04)' }}>
                        <td className="px-6 py-4">
                          <div className="font-medium" style={{ color: '#F4F6FA', fontFamily: 'IBM Plex Sans' }}>{lead.name}</div>
                          {lead.phone && <div className="text-xs mt-0.5" style={{ color: '#4B5563', fontFamily: 'Azeret Mono' }}>{lead.phone}</div>}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-xs" style={{ color: '#9CA3AF', fontFamily: 'Azeret Mono' }}>{lead.email}</div>
                        </td>
                        <td className="px-6 py-4">
                          {lead.ip_address ? (
                            <div
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg"
                              style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}
                            >
                              <Wifi size={11} style={{ color: '#6B7B8E' }} />
                              <span className="text-xs font-mono" style={{ color: '#C4CFD8', fontFamily: 'Azeret Mono' }}>{lead.ip_address}</span>
                            </div>
                          ) : (
                            <span className="text-xs" style={{ color: '#4B5563', fontFamily: 'Azeret Mono' }}>—</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-xs" style={{ color: '#6B7B8E', fontFamily: 'Azeret Mono' }}>
                            {new Date(lead.created_at).toLocaleDateString()}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {lead.ip_address ? (
                            <Button
                              data-testid={`rewind-toggle-${lead.id}`}
                              onClick={() => handleToggleRewind(lead)}
                              disabled={rewindPending[lead.id]}
                              className="h-8 px-3 text-xs rounded-lg inline-flex items-center gap-1.5 font-medium"
                              style={{
                                background: lead.rewind_allowed ? 'rgba(220,38,38,0.1)' : 'rgba(30,64,175,0.12)',
                                color: lead.rewind_allowed ? '#FCA5A5' : '#93C5FD',
                                border: `1px solid ${lead.rewind_allowed ? 'rgba(220,38,38,0.25)' : 'rgba(30,64,175,0.25)'}`,
                                fontFamily: 'IBM Plex Sans',
                              }}
                            >
                              {lead.rewind_allowed
                                ? <><ShieldOff size={12} /> Revoke</>
                                : <><Shield size={12} /> Allow Rewind</>}
                            </Button>
                          ) : (
                            <span className="text-xs" style={{ color: '#4B5563', fontFamily: 'Azeret Mono' }}>No IP</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── ANALYTICS TAB ── */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">

            {/* Video Funnel */}
            <div className="rounded-2xl p-6" style={{ backgroundColor: '#0B1020', border: '1px solid rgba(212,175,55,0.12)' }}>
              <div className="flex items-center gap-2 mb-5">
                <TrendingUp size={15} style={{ color: '#D4AF37' }} />
                <h2 className="text-base font-bold" style={{ fontFamily: 'Space Grotesk', color: '#F4F6FA' }}>Video Watch Funnel</h2>
              </div>
              {analytics ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {[25, 50, 75, 90].map(m => {
                    const count = analytics.video_funnel[String(m)] || 0;
                    const max = Math.max(analytics.video_funnel['25'] || 1, 1);
                    const pct = Math.round((count / max) * 100);
                    return (
                      <div
                        key={m}
                        className="p-4"
                        style={{ backgroundColor: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)' }}
                      >
                        <div className="text-xs mb-1" style={{ fontFamily: 'Azeret Mono', color: '#6B7B8E', letterSpacing: '0.06em' }}>
                          {m}% watched
                        </div>
                        <div className="text-2xl font-bold mb-3" style={{ fontFamily: 'Space Grotesk', color: '#F4F6FA' }}>{count}</div>
                        <div className="h-1 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}>
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${pct}%`,
                              background: m === 90
                                ? 'linear-gradient(90deg, #2563EB, #60A5FA)'
                                : 'rgba(30,64,175,0.55)',
                              transition: 'width 0.6s ease',
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="py-8 text-center text-sm" style={{ color: '#4B5563', fontFamily: 'IBM Plex Sans' }}>Loading...</div>
              )}
            </div>

            {/* Zoom Bookings + Video Completions */}
            {analytics && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="rounded-2xl p-5" style={{ backgroundColor: '#0B1020', border: '1px solid rgba(212,175,55,0.12)' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <CalendarCheck size={15} style={{ color: '#22C55E' }} />
                    <span className="text-xs" style={{ fontFamily: 'Azeret Mono', color: '#6B7B8E', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Zoom Bookings</span>
                  </div>
                  <div className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk', color: '#F4F6FA' }}>{analytics.booking_count}</div>
                </div>
                <div className="rounded-2xl p-5" style={{ backgroundColor: '#0B1020', border: '1px solid rgba(212,175,55,0.12)' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <Video size={15} style={{ color: '#A78BFA' }} />
                    <span className="text-xs" style={{ fontFamily: 'Azeret Mono', color: '#6B7B8E', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Watched 90%+</span>
                  </div>
                  <div className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk', color: '#F4F6FA' }}>{analytics.video_funnel?.['90'] ?? 0}</div>
                </div>
              </div>
            )}

            {/* Video Viewers Table */}
            {analytics && analytics.video_viewers?.length > 0 && (
              <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: '#0B1020', border: '1px solid rgba(212,175,55,0.12)' }}>
                <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                  <h2 className="text-base font-bold" style={{ fontFamily: 'Space Grotesk', color: '#F4F6FA' }}>Video Viewers</h2>
                  <span
                    className="text-xs px-2.5 py-1 rounded-full"
                    style={{ fontFamily: 'Azeret Mono', color: '#A78BFA', backgroundColor: 'rgba(167,139,250,0.08)', border: '1px solid rgba(167,139,250,0.18)' }}
                  >
                    {analytics.video_viewers.length} records
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                        {['IP', 'Name', 'Email', 'Milestone', 'Date'].map(h => (
                          <th
                            key={h}
                            className="text-left px-6 py-3 text-xs font-medium"
                            style={{ fontFamily: 'Azeret Mono', color: '#4B5563', letterSpacing: '0.07em', textTransform: 'uppercase' }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.video_viewers.map((v, i) => (
                        <tr key={i} className="border-b last:border-0" style={{ borderColor: 'rgba(255,255,255,0.04)' }}>
                          <td className="px-6 py-3">
                            <span className="text-xs font-mono" style={{ color: '#C4CFD8', fontFamily: 'Azeret Mono' }}>{v.ip}</span>
                          </td>
                          <td className="px-6 py-3">
                            <span className="text-xs" style={{ color: '#D1D5DB', fontFamily: 'IBM Plex Sans' }}>{v.name || '—'}</span>
                          </td>
                          <td className="px-6 py-3">
                            <span className="text-xs" style={{ color: '#9CA3AF', fontFamily: 'Azeret Mono' }}>{v.email || '—'}</span>
                          </td>
                          <td className="px-6 py-3">
                            <span
                              className="inline-flex items-center px-2 py-0.5 text-xs font-bold rounded"
                              style={{
                                fontFamily: 'Azeret Mono',
                                backgroundColor: v.milestone === 90 ? 'rgba(30,64,175,0.18)' : 'rgba(255,255,255,0.05)',
                                color: v.milestone === 90 ? '#60A5FA' : '#9CA3AF',
                                border: `1px solid ${v.milestone === 90 ? 'rgba(30,64,175,0.35)' : 'rgba(255,255,255,0.08)'}`,
                              }}
                            >
                              {v.milestone}%
                            </span>
                          </td>
                          <td className="px-6 py-3">
                            <span className="text-xs" style={{ color: '#6B7B8E', fontFamily: 'Azeret Mono' }}>
                              {new Date(v.timestamp).toLocaleDateString()}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Recent Visitors Table */}
            {analytics && analytics.recent_visits?.length > 0 && (
              <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: '#0B1020', border: '1px solid rgba(212,175,55,0.12)' }}>
                <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                  <h2 className="text-base font-bold" style={{ fontFamily: 'Space Grotesk', color: '#F4F6FA' }}>Recent Visitors</h2>
                  <span
                    className="text-xs px-2.5 py-1 rounded-full"
                    style={{ fontFamily: 'Azeret Mono', color: '#6B7B8E', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}
                  >
                    last 60
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                        {['IP Address', 'Page', 'Time'].map(h => (
                          <th
                            key={h}
                            className="text-left px-6 py-3 text-xs font-medium"
                            style={{ fontFamily: 'Azeret Mono', color: '#4B5563', letterSpacing: '0.07em', textTransform: 'uppercase' }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.recent_visits.map((v, i) => (
                        <tr key={i} className="border-b last:border-0" style={{ borderColor: 'rgba(255,255,255,0.04)' }}>
                          <td className="px-6 py-3">
                            <span className="text-xs font-mono" style={{ color: '#C4CFD8', fontFamily: 'Azeret Mono' }}>{v.ip}</span>
                          </td>
                          <td className="px-6 py-3">
                            <span
                              className="inline-flex items-center px-2 py-0.5 text-xs rounded"
                              style={{
                                fontFamily: 'Azeret Mono',
                                backgroundColor: v.page === '/webinar' ? 'rgba(30,64,175,0.1)' : 'rgba(255,255,255,0.04)',
                                color: v.page === '/webinar' ? '#93C5FD' : '#9CA3AF',
                                border: `1px solid ${v.page === '/webinar' ? 'rgba(30,64,175,0.2)' : 'rgba(255,255,255,0.07)'}`,
                              }}
                            >
                              {v.page}
                            </span>
                          </td>
                          <td className="px-6 py-3">
                            <span className="text-xs" style={{ color: '#6B7B8E', fontFamily: 'Azeret Mono' }}>
                              {new Date(v.timestamp).toLocaleDateString()}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {analytics && !analytics.recent_visits?.length && !analytics.video_viewers?.length && (
              <div
                className="py-12 text-center text-sm rounded-2xl"
                style={{ backgroundColor: '#0B1020', border: '1px solid rgba(212,175,55,0.12)', color: '#4B5563', fontFamily: 'IBM Plex Sans' }}
              >
                No visitor data yet. Analytics will appear as users visit your site.
              </div>
            )}
          </div>
        )}

        {/* ── SETTINGS TAB ── */}
        {activeTab === 'settings' && (
          <div className="rounded-2xl p-8" style={{ backgroundColor: '#0B1020', border: '1px solid rgba(212,175,55,0.18)' }}>
            <div className="flex items-center gap-3 mb-6">
              <Settings size={18} style={{ color: '#D4AF37' }} />
              <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk', color: '#F4F6FA' }}>Page Settings</h2>
            </div>
            <div className="space-y-5">
              <div>
                <Label className="text-xs mb-1.5 block" style={{ color: '#C4CFD8', fontFamily: 'IBM Plex Sans' }}>YouTube Webinar URL</Label>
                <Input
                  data-testid="admin-youtube-url-input"
                  value={settings.youtube_url}
                  onChange={e => setSettings(p => ({ ...p, youtube_url: e.target.value }))}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="h-11 rounded-xl"
                  style={{ backgroundColor: '#070A0F', borderColor: 'rgba(212,175,55,0.18)', color: '#F4F6FA' }}
                />
              </div>
              <div>
                <Label className="text-xs mb-1.5 block" style={{ color: '#C4CFD8', fontFamily: 'IBM Plex Sans' }}>Calendly Booking URL</Label>
                <Input
                  data-testid="admin-calendly-url-input"
                  value={settings.calendly_url}
                  onChange={e => setSettings(p => ({ ...p, calendly_url: e.target.value }))}
                  placeholder="https://calendly.com/your-username/strategy-call"
                  className="h-11 rounded-xl"
                  style={{ backgroundColor: '#070A0F', borderColor: 'rgba(212,175,55,0.18)', color: '#F4F6FA' }}
                />
              </div>
              <Button
                data-testid="admin-save-button"
                onClick={handleSave}
                disabled={saveLoading}
                className="h-11 px-6 rounded-xl font-semibold inline-flex items-center gap-2"
                style={{ background: 'linear-gradient(135deg, #2563EB 0%, #1E40AF 60%, #1e3a8a 100%)', color: '#FFFFFF', fontFamily: 'IBM Plex Sans', border: 'none' }}
              >
                <Save size={15} />
                {saveLoading ? 'Saving...' : 'Save Settings'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
